/// <reference types="google_interactive_media_ads_types" />

'use client';

import { c as _c } from "react/compiler-runtime";
import { usePostHog } from '@posthog/next';
import { Volume2, VolumeX } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import AdResumeOverlay from '@/app/[locale]/(worlds)/[roomId]/components/AdResumeOverlay';
import { FEED_TRACKING_PARAMS } from '@/constants/feed';
import { VastAdStateProvider } from '@/context/VastAdStateContext';
import { usePlatformAdFree } from '@/hooks/usePlatformAdFree';
import { createVastAdTelemetry, type VastTelemetryEvent } from '@/lib/ad-telemetry/vast';
import { env } from '@/lib/runtime-env';
import { type AdTelemetryConfig } from '@/types/ad-telemetry';
import { useTranslations } from 'next-intl';
const AD_LOADING_TIMEOUT_MS = 4000;
const SDK_PENDING_TIMEOUT_MS = 5000;
const hasImaSdk = () => typeof window !== 'undefined' && !!window.google?.ima;
type VastAdOverlayProps = {
  children?: ReactNode;
  preload: boolean;
  loadingPlaceholder?: ReactNode;
  telemetry?: AdTelemetryConfig;
};
function VastAdOverlay(t0) {
  const $ = _c(47);
  const {
    children,
    preload,
    loadingPlaceholder,
    telemetry
  } = t0;
  const t = useTranslations();
  const posthog = usePostHog();
  const {
    isAdFree
  } = usePlatformAdFree();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = env("NEXT_PUBLIC_VAST_TAG_URL") ?? "";
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const vastTagUrl = t1;
  let t2;
  if ($[1] !== searchParams) {
    t2 = searchParams.get("source");
    $[1] = searchParams;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isFeedSourcedTraffic = t2 === FEED_TRACKING_PARAMS.source;
  const isVastEnabled = !isFeedSourcedTraffic && !isAdFree && !!vastTagUrl;
  const [adState, setAdState] = useState("pre");
  const [sdkReady, setSdkReady] = useState(hasImaSdk);
  const adActive = isVastEnabled && adState !== "done";
  const [isAdsManagerStarted, setIsAdsManagerStarted] = useState(false);
  const [showResumeOverlay, setShowResumeOverlay] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const adContainerRef = useRef(null);
  const adsManagerRef = useRef(null);
  const adsLoaderRef = useRef(null);
  const adDisplayContainerRef = useRef(null);
  const visibilityListenerRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const progressBarRef = useRef(null);
  const adDurationRef = useRef(-1);
  const telemetryRef = useRef(telemetry);
  const vastTelemetryRef = useRef(null);
  let t3;
  let t4;
  if ($[3] !== telemetry) {
    t3 = () => {
      telemetryRef.current = telemetry;
    };
    t4 = [telemetry];
    $[3] = telemetry;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== posthog) {
    t5 = () => {
      vastTelemetryRef.current = createVastAdTelemetry({
        capture: event => {
          posthog.capture(event.event, event.properties);
        },
        isEventEnabled: event_0 => telemetryRef.current?.[event_0] ?? false
      });
    };
    t6 = [posthog];
    $[6] = posthog;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== isMuted) {
    t7 = () => {
      if (!adsManagerRef.current) {
        return;
      }
      const newMuted = !isMuted;
      adsManagerRef.current.setVolume(newMuted ? 0 : 1);
      setIsMuted(newMuted);
    };
    $[9] = isMuted;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const toggleMute = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      if (!adsManagerRef.current) {
        return;
      }
      try {
        adsManagerRef.current.resume();
        setShowResumeOverlay(false);
      } catch {
        setShowResumeOverlay(true);
      }
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const tryResumeAd = t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (visibilityListenerRef.current) {
        return;
      }
      visibilityListenerRef.current = () => {
        if (document.visibilityState === "visible") {
          tryResumeAd();
        }
      };
      document.addEventListener("visibilitychange", visibilityListenerRef.current);
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const setupVisibilityListener = t9;
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      if (visibilityListenerRef.current) {
        document.removeEventListener("visibilitychange", visibilityListenerRef.current);
        visibilityListenerRef.current = null;
      }
      if (adsManagerRef.current) {
        adsManagerRef.current.destroy();
        adsManagerRef.current = null;
      }
      if (adsLoaderRef.current) {
        adsLoaderRef.current.destroy();
        adsLoaderRef.current = null;
      }
      if (adDisplayContainerRef.current) {
        adDisplayContainerRef.current.destroy();
        adDisplayContainerRef.current = null;
      }
    };
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const cleanup = t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setAdState("done");
      cleanup();
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const finishAd = t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      if (!adsManagerRef.current || !adContainerRef.current || !adDisplayContainerRef.current) {
        vastTelemetryRef.current?.handleFailure({
          failureStage: "manager_start",
          isEmpty: false,
          isBlocked: false
        });
        finishAd();
        return;
      }
      try {
        adDisplayContainerRef.current.initialize();
        const {
          width,
          height
        } = adContainerRef.current.getBoundingClientRect();
        adsManagerRef.current.init(Math.floor(width), Math.floor(height));
        adsManagerRef.current.start();
        setIsAdsManagerStarted(true);
        setShowResumeOverlay(false);
      } catch {
        vastTelemetryRef.current?.handleFailure({
          failureStage: "manager_start",
          isEmpty: false,
          isBlocked: false
        });
        finishAd();
      }
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const startAdsManager = t12;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      if (adsLoaderRef.current !== null) {
        return;
      }
      if (!adContainerRef.current || !hasImaSdk()) {
        vastTelemetryRef.current?.handleFailure({
          failureStage: "sdk_script",
          isEmpty: true,
          isBlocked: true
        });
        finishAd();
        return;
      }
      google.ima.settings.setDisableCustomPlaybackForIOS10Plus(true);
      const adDisplayContainer = new google.ima.AdDisplayContainer(adContainerRef.current);
      adDisplayContainerRef.current = adDisplayContainer;
      const adsLoader = new google.ima.AdsLoader(adDisplayContainer);
      adsLoaderRef.current = adsLoader;
      adsLoader.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, (event_1 => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        loadingTimeoutRef.current = setTimeout(() => {
          console.warn("[VastAdOverlay] ADS_MANAGER_LOADED timeout: no ad event received within", AD_LOADING_TIMEOUT_MS, "ms");
          vastTelemetryRef.current?.handleFailure({
            failureStage: "manager_event_timeout",
            isEmpty: false,
            isBlocked: false
          });
          finishAd();
        }, AD_LOADING_TIMEOUT_MS);
        const settings = new google.ima.AdsRenderingSettings();
        settings.enablePreloading = true;
        settings.uiElements = [];
        const adsManager = event_1.getAdsManager({
          currentTime: 0
        }, settings);
        adsManagerRef.current = adsManager;
        adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, errorEvent => {
          vastTelemetryRef.current?.handleFailure({
            failureStage: "ads_manager",
            isEmpty: false,
            isBlocked: false,
            error: errorEvent.getError()
          });
          finishAd();
        });
        adsManager.addEventListener(google.ima.AdEvent.Type.AD_BREAK_FETCH_ERROR, () => {
          vastTelemetryRef.current?.handleFailure({
            failureStage: "ads_manager",
            isEmpty: false,
            isBlocked: false
          });
          finishAd();
        });
        adsManager.addEventListener(google.ima.AdEvent.Type.LOADED, adEvent => {
          vastTelemetryRef.current?.handleLoaded();
          if (!adEvent.getAd()?.isLinear()) {
            finishAd();
          }
        });
        adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, event_2 => {
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
          vastTelemetryRef.current?.handleStarted();
          adDurationRef.current = event_2.getAd()?.getDuration() ?? -1;
        });
        adsManager.addEventListener(google.ima.AdEvent.Type.AD_PROGRESS, () => {
          if (!progressBarRef.current || !adsManagerRef.current) {
            return;
          }
          const duration = adDurationRef.current;
          const remaining = adsManagerRef.current.getRemainingTime();
          if (duration > 0 && remaining >= 0) {
            const pct = (1 - remaining / duration) * 100;
            progressBarRef.current.style.width = `${pct}%`;
          }
        });
        adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
          setAdState("playing");
          setupVisibilityListener();
        });
        adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
          finishAd();
        });
        adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
          finishAd();
        });
        adsManager.addEventListener(google.ima.AdEvent.Type.COMPLETE, () => {
          finishAd();
        });
        adsManager.addEventListener(google.ima.AdEvent.Type.SKIPPED, () => {
          finishAd();
        });
        adsManager.setVolume(0);
        startAdsManager();
      }) as () => void, false);
      adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, errorEvent_0 => {
        vastTelemetryRef.current?.handleAdsLoaderError(errorEvent_0.getError());
        finishAd();
      }, false);
      const adsRequest = new google.ima.AdsRequest();
      adsRequest.adTagUrl = vastTagUrl;
      adsRequest.linearAdSlotWidth = 640;
      adsRequest.linearAdSlotHeight = 360;
      adsRequest.nonLinearAdSlotWidth = 640;
      adsRequest.nonLinearAdSlotHeight = 150;
      adsRequest.setAdWillAutoPlay(true);
      adsRequest.setAdWillPlayMuted(true);
      adsLoader.requestAds(adsRequest);
      loadingTimeoutRef.current = setTimeout(() => {
        console.warn("[VastAdOverlay] requestAds timeout: ADS_MANAGER_LOADED not received within", AD_LOADING_TIMEOUT_MS, "ms");
        vastTelemetryRef.current?.handleFailure({
          failureStage: "ads_loader",
          isEmpty: false,
          isBlocked: false
        });
        finishAd();
      }, AD_LOADING_TIMEOUT_MS);
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const initializeAds = t13;
  let t14;
  let t15;
  if ($[17] !== adActive || $[18] !== sdkReady) {
    t14 = () => {
      if (!adActive) {
        return;
      }
      vastTelemetryRef.current?.startRequest({
        hasVastTagUrl: !!vastTagUrl,
        isSdkReadyAtRequest: sdkReady
      });
    };
    t15 = [adActive, sdkReady, vastTagUrl];
    $[17] = adActive;
    $[18] = sdkReady;
    $[19] = t14;
    $[20] = t15;
  } else {
    t14 = $[19];
    t15 = $[20];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[21] !== isVastEnabled || $[22] !== sdkReady) {
    t16 = () => {
      if (!isVastEnabled || !sdkReady) {
        return;
      }
      initializeAds();
      return () => {
        cleanup();
      };
    };
    t17 = [isVastEnabled, sdkReady, initializeAds, cleanup];
    $[21] = isVastEnabled;
    $[22] = sdkReady;
    $[23] = t16;
    $[24] = t17;
  } else {
    t16 = $[23];
    t17 = $[24];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[25] !== adActive || $[26] !== sdkReady) {
    t18 = () => {
      if (!adActive || sdkReady) {
        return;
      }
      const timeoutId = window.setTimeout(() => {
        console.warn("[VastAdOverlay] IMA SDK timeout: SDK not ready within", SDK_PENDING_TIMEOUT_MS, "ms");
        vastTelemetryRef.current?.handleFailure({
          failureStage: "sdk_timeout",
          isEmpty: true,
          isBlocked: true
        });
        finishAd();
      }, SDK_PENDING_TIMEOUT_MS);
      return () => window.clearTimeout(timeoutId);
    };
    t19 = [adActive, sdkReady, finishAd];
    $[25] = adActive;
    $[26] = sdkReady;
    $[27] = t18;
    $[28] = t19;
  } else {
    t18 = $[27];
    t19 = $[28];
  }
  useEffect(t18, t19);
  let t20;
  if ($[29] !== adState) {
    t20 = () => {
      if (adState !== "playing") {
        return;
      }
      const handleResize = () => {
        if (!adsManagerRef.current || !adContainerRef.current) {
          return;
        }
        const {
          width: width_0,
          height: height_0
        } = adContainerRef.current.getBoundingClientRect();
        adsManagerRef.current.resize(Math.floor(width_0), Math.floor(height_0));
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    $[29] = adState;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== adState || $[32] !== isVastEnabled) {
    t21 = [isVastEnabled, adState];
    $[31] = adState;
    $[32] = isVastEnabled;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  useEffect(t20, t21);
  const t22 = preload ? children : !adActive && children;
  let t23;
  if ($[34] !== adActive || $[35] !== adState || $[36] !== isAdsManagerStarted || $[37] !== isMuted || $[38] !== loadingPlaceholder || $[39] !== showResumeOverlay || $[40] !== t || $[41] !== toggleMute) {
    t23 = adActive && <><Script src="https://imasdk.googleapis.com/js/sdkloader/ima3.js" strategy="afterInteractive" onReady={() => setSdkReady(true)} onError={() => {
        vastTelemetryRef.current?.handleFailure({
          failureStage: "sdk_script",
          isEmpty: true,
          isBlocked: true
        });
        setAdState("done");
      }} /><div className="absolute inset-0 z-30 bg-black">{adState === "pre" && loadingPlaceholder}{adState === "pre" && !showResumeOverlay && <div className="absolute inset-0 flex items-center justify-center"><div className="z-999 h-8 w-8 animate-spin rounded-full border-4 border-white/30 border-t-white" /></div>}<div ref={adContainerRef} className="relative h-full w-full">{showResumeOverlay && <AdResumeOverlay onResume={isAdsManagerStarted ? tryResumeAd : startAdsManager} />}{adState === "playing" && <><div className="absolute bottom-0 left-0 w-full overflow-hidden"><div ref={progressBarRef} className="h-1 bg-[#30fdfe] transition-[width] duration-250 ease-linear" style={{
                width: "0%"
              }} /></div><div className="absolute bottom-4 left-4 z-999 rounded bg-black/60 px-2 py-0.5 text-base font-bold text-white">{t("vast-ad-overlay.ad-label")}</div></>}</div>{adState === "playing" && <button onClick={toggleMute} className="absolute top-4 left-4 z-999 rounded-full bg-black/50 p-2 text-white hover:bg-black/70">{isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}</button>}</div></>;
    $[34] = adActive;
    $[35] = adState;
    $[36] = isAdsManagerStarted;
    $[37] = isMuted;
    $[38] = loadingPlaceholder;
    $[39] = showResumeOverlay;
    $[40] = t;
    $[41] = toggleMute;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== adActive || $[44] !== t22 || $[45] !== t23) {
    t24 = <VastAdStateProvider adActive={adActive}>{t22}{t23}</VastAdStateProvider>;
    $[43] = adActive;
    $[44] = t22;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  return t24;
}
export default VastAdOverlay;